import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Calendar,
  Trophy,
  ListChecks,
  BarChart3,
  Camera,
  LogOut,
  Shield,
  Sparkles,
  Globe,
  Server,
  Settings as SettingsIcon,
  CreditCard as BillingIcon,
  BookOpen,
} from "lucide-react";
import ClubHeader from "@/components/ClubHeader";
import TrialBanner from "@/components/TrialBanner";
import MobileBottomNav from "@/components/MobileBottomNav";
import WhatsNewPopover from "@/components/WhatsNewPopover";
import KeyboardShortcuts from "@/components/KeyboardShortcuts";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/photographers", label: "Photographers", icon: Camera },
  { to: "/seasons", label: "Seasons", icon: Calendar },
  { to: "/competitions", label: "Competitions", icon: Trophy },
  { to: "/results", label: "Results", icon: ListChecks },
  { to: "/stats", label: "Statistics", icon: BarChart3, hideForRoles: ["VIEWER"] },
  { to: "/awards", label: "Season Stats", icon: Sparkles, hideForRoles: ["VIEWER"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <aside
        className="hidden xl:flex w-64 shrink-0 flex-col"
        style={{ background: "#0d0d0d", borderRight: "1px solid #1f1f1f" }}
        data-testid="sidebar"
      >
        <div className="px-6 py-6" style={{ borderBottom: "1px solid #1f1f1f" }}>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
            Camera Club
          </div>
          <div className="text-lg font-semibold tracking-tight mt-1">Score Console</div>
        </div>

        <nav className="flex-1 py-4">
          {NAV.filter((item) => !item.hideForRoles?.includes(user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon size={18} strokeWidth={1.6} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {user?.role === "ADMIN" && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
              data-testid="nav-users"
            >
              <Shield size={18} strokeWidth={1.6} />
              <span>Users</span>
            </NavLink>
          )}
          {user?.role === "ADMIN" && (
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
              data-testid="nav-settings"
            >
              <SettingsIcon size={18} strokeWidth={1.6} />
              <span>Settings</span>
            </NavLink>
          )}
          {user?.role === "ADMIN" && (
            <NavLink
              to="/billing"
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
              data-testid="nav-billing"
            >
              <BillingIcon size={18} strokeWidth={1.6} />
              <span>Billing</span>
            </NavLink>
          )}
          {user?.role === "SUPERADMIN" && (
            <NavLink
              to="/platform"
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
              data-testid="nav-platform"
            >
              <Server size={18} strokeWidth={1.6} />
              <span>Platform</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4" style={{ borderTop: "1px solid #1f1f1f" }}>
          <a
            href="/portal" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 muted-text text-xs mb-2 hover:text-white"
            data-testid="open-portal-link"
          >
            <Globe size={12} /> Public portal
          </a>
          <a
            href="/user-manual.pdf" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 muted-text text-xs mb-2 hover:text-white"
            data-testid="open-user-manual-link"
          >
            <BookOpen size={12} /> User manual (PDF)
          </a>
          <div
            className="flex items-center gap-2 muted-text text-xs mb-3"
            data-testid="keyboard-shortcuts-hint"
          >
            Press
            <kbd
              className="mono text-[10px] px-1.5 py-0.5 rounded border"
              style={{ background: "#1f1f1f", borderColor: "#2f2f2f", color: "#fbbf24" }}
            >?</kbd>
            for keyboard shortcuts
          </div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          <div className="text-xs muted-text truncate mb-3" data-testid="current-user-role">
            {user?.email} · {user?.role}
          </div>
          <button
            onClick={doLogout}
            className="ccs-btn-ghost w-full justify-center flex items-center gap-2 text-sm"
            data-testid="logout-button"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col overflow-x-auto pb-16 xl:pb-0">
        <TrialBanner />
        <ClubHeader canEdit={user?.role === "ADMIN"} />
        <div className="flex-1 min-w-0 overflow-x-auto">
          <Outlet />
        </div>
      </main>
      <MobileBottomNav user={user} onLogout={doLogout} />
      <WhatsNewPopover />
      <KeyboardShortcuts />
    </div>
  );
}
